import dayjs from 'dayjs';
import { getPercentages } from '../../Colors/percentages.js';
import {trainingAPI} from '../../api/TrainingAPI.js';
import { 
  getRestTravelLabel,
  getBarColorsForHighcharts,
  getBarWeightMetric,
  isVBTMetric,
  getValueColor,
  getRestAndTravelDaysInChart,
  addPredictionSeriesConfig
} from '../../Utils/chart_utils.jsx';
import { get_unit, get_dist_unit } from "../../Utils/unit.js";
import { chartExportableOptions, getSuffixByStat } from '../mapperUtil.js'
import { drilldownPlayerLabel } from '../drilldownPlayerLabel.js';
import { healthyMax, healthyMean, healthyMin, healthySum, isInjured } from '../../Utils/injury.mjs';
import { color } from 'highcharts';
export const mapDataForFPTestingHighcharts = ({
  dataIn,
  metric,
  showACWR = false,
  teamName,
  showPredictions = false,
  teamColors = [],
  isTeamChart = false,
  showAvgPlotline = false,
  singleAxis = false,
  isForcePlateMetric = false,
  isTestingMetric = false,
  isSubjectiveMetric = false,
  isDrillData = false,
  isRPEChart = false,
  games = [],
  showRest = false,
  showTravel = false,
  restAndTravel = null,
  startDate = null,
  endDate = null,
  session = [], 
  playerGroup = [],
  position = [],
  stat = null,
  teamMetric = null,
  showTitle = true,
  enableDrillDown = false,
  showHamburger = true,
  dataColorUtil = null,
  selectedPlayerId = null
}) => {
  const injuredInGroup = selectedPlayerId ? () => false : isInjured;
  const metricKey = metric?.metric_type === 'forceplate' ? metric?.key : (
    metric?.key.startsWith('testing-')
    ?
    metric?.name
    :
    metric?.key
  );

  if (!metricKey) {
    console.error(`Metric ${metricKey} not found`);
    return null;
  }

  let tooltip = {
    shared: true
  };
  tooltip = getCustomTooltip(isForcePlateMetric, isSubjectiveMetric, isRPEChart, isTestingMetric, tooltip)

  let data = dataIn?.data || [];
  const colors_100 = dataIn?.colors;
  const categories = [];
  const series = [];
  const predictionSeries = [];
  const secondarySeriesData = [];

  const healthyData = data.filter((dp) => !injuredInGroup(dp));
  const max_value = Math.max(
    ...(healthyData.map((dp) => isForcePlateMetric ? dp[metricKey + "__max"] : dp[metricKey]).filter((x) => x) || [])
  );
  const min_value = Math.min(
    ...(healthyData.map((dp) => isForcePlateMetric ? dp[metricKey + "__min"] : dp[metricKey]).filter((x) => x) || [])
  );

  const best_value = metric?.low_good ? min_value : max_value;			
  const isVBT = isVBTMetric(metric?.name);
  const barWeightMetric = data.length && isTestingMetric ? getBarWeightMetric(metric?.name, Object.keys(data[0])) : null
  const useMaxBarWt = (teamName == "Georgia Tech Football")

  data.sort((a, b) => {
    if(isTeamChart && !isDrillData) {
		  return a[metricKey] > b[metricKey] ? -1 : 1;
    } else {
      return a.date < b.date ? -1 : 1;
    }
	});

  data.forEach((item, index) => {
    if(isTeamChart) {
      categories.push(item.name);
    } else {
      const opponent = games?.find((x) => x.date === item.date)?.opponent || ''
      categories.push(opponent || dayjs(item.date).format('MM-DD'));
    }

    if (isForcePlateMetric) {
      const max = item[`${metric.name}__max`];
      const min = item[`${metric.name}__min`];
      const diff = item[`${metric.name}__diff`];
      const avg = item[`${metric.name}__avg`];
      const sum = item[`${metric.name}__sum`];

      const val = item[metric.name] || item[`${metric.name}__avg`];
      const is_max = val === best_value;
      const color = item.color

      let y = avg;
      if (typeof(stat) === 'string' && stat.toLowerCase() === 'max') {
        y = ((metric.low_good ?? false) ? min : max)
      } else if (typeof(stat) === 'string' && stat.toLowerCase() === 'sum') {
        y = sum
      }

      series.push({ y, color, is_max, max, min, diff, custom: { injured: injuredInGroup(item) }, ...(enableDrillDown && {drilldown: `drilldown-${index}`})});
      secondarySeriesData.push([min, max]);
    } else if (isTestingMetric) {
      const avg = item[metric.name];
      const max = item[`${metric.name}__max`];
      const min = item[`${metric.name}__min`];
      const sum = item[`${metric.name}__sum`];

      const val = item[metricKey] || item[`${metricKey}__avg`];
      const color = item.color

      let value = avg;
      if (typeof(stat) === 'string' && stat.toLowerCase() === 'sum') {
        value = sum;
      } else if (typeof(stat) === 'string' && stat.toLowerCase() === 'max') {
        value = ((metric.low_good ?? false) ? min : max);
      }

      series.push({y: value, max, min, color, custom: { injured: injuredInGroup(item) }, ...(enableDrillDown && {drilldown: `drilldown-${index}`})})

      if(isVBT && barWeightMetric) {
        const barWeightValue = item[barWeightMetric];
        secondarySeriesData.push(barWeightValue ?? null)
      }
    } 
  });

  const maxValue = Math.max(
    ...(showPredictions ? predictionSeries.filter(v => v != null && !isNaN(v)) : []),
    ...(isForcePlateMetric ? secondarySeriesData.filter(v => v[1] != null && !isNaN(v[1])).map( (v) => v[1]) : []),
    ...series.filter(v => v.y != null && !isNaN(v.y)).map( (v) => v.y)
  );

  let has_units = metric.units;
  let unit = "";
  if (has_units) {
    unit = get_unit(metricKey, metric, teamName, teamMetric);
    if (metricKey == "distance") {
      unit = get_dist_unit(teamName, teamMetric);
    }
  }
  const yAxisTitle = (has_units ? unit : "")
  const maximumSeriesValue = isFinite(maxValue) ? Math.ceil(maxValue * 1.1) : 100;

  const minValue = Math.min(
    ...(showPredictions ? predictionSeries.filter(v => v != null && !isNaN(v)) : []),
    ...(isForcePlateMetric ? secondarySeriesData.filter(v => v[0] != null && !isNaN(v[0])).map( (v) => v[0]) : []),
    ...series.filter(v => v.y != null && !isNaN(v.y)).map( (v) => v.y)
  );
  const minimumSeriesValue = isFinite(minValue) ? Math.floor(minValue * 1.1) : 0;

  const mainSeries = {
    name: metric?.name || metric?.short || '',
    type: 'column',
    yAxis: 0,
    data: series,
    colorByPoint: false,
    dataLabels: {
      style: {
        textOutline: 'none'
      }
    }
  }

  if(isSubjectiveMetric) {
    mainSeries.dataLabels = {
      ...mainSeries.dataLabels,
      align: 'center',
      rotation: series.length > 4 ? 270 : 0,
      ...(isRPEChart ? {} : {format: '{point.text}'})
    }
  }

  let secondarySeries = {
    dataLabels: {
      style: {
        textOutline: 'none'
      }
    }
  };
  let secondaryMax = 0;
  if(isTestingMetric){
    const max = Math.max(
      ...secondarySeriesData.filter(v => v != null && !isNaN(v))
    );
    secondaryMax = isFinite(max) ? Math.ceil(max * 1.1) : 0;
    secondarySeries = {
      ...secondarySeries,
      name: barWeightMetric,
      type: 'spline',
      data: secondarySeriesData,
      color: 'rgb(70, 170, 197)',
      yAxis: 1,
      showInLegend: secondarySeriesData.length ? true : false,
    }
  } else if (isForcePlateMetric) {
    secondarySeries = {
      ...secondarySeries,
      name: 'Error',
      type: 'errorbar',
      data: secondarySeriesData,
      yAxis: 0,
    }
  }

  mainSeries.data = mainSeries.data.map((item, index) => ({
    ...item,
    ...(enableDrillDown && {drilldown: categories[index]}),
    name: categories[index]
  }));

  const getXAxisLabelRotation = (chart, labelVals) => {
    chart.update({
      xAxis: {
        labels: (
          labelVals.length > 20
          ? 
          {
            rotation: -90,
            overflow: 'justify',
            staggerLines: 1,
            step: 0
          }
          : 
          (
            labelVals.length > 10
            ? 
            {
              rotation: -45,
              overflow: 'justify',
              staggerLines: 1,
              step: 0
            }
            : 
            {
              rotation: 0,
            }
          )
        ),
      }
    });
        
  }

  let positionsArray = [];
  let playerNamesArray = [];
  let lastDrillup = 0;
  let lastLevel = 0;
  const DRILLUP_DEBOUNCE_MS = 50;
  let options = {
    chart: {
      type: 'column',
      zoomType: 'xy',
      events: {
        load: function() {
          // Store the original series data, ACWR series, and x-axis configuration
          this.isDrilling = false;
          this.options.custom = this.options.custom || {};
          this.options.custom.title = (showTitle ? (metric?.name || metric?.short || '') : '');
        },
        drilldown: function(e) {
          if (this.isDrilling) return;
          this.isDrilling = true;
          this.options.custom = this.options.custom || {};
          if (!e.seriesOptions) {
            const chart = this;
            const date = data[e.point.index]?.date;
    
            if (!chart.drilldownLevel || chart.drilldownLevel < 0) {
              chart.drilldownLevel = 0;
            }

            if (chart.drilldownLevel >= 3) {
              return; 
            }

            if (!chart.drilldownData && chart.drilldownLevel === 0) {
              chart.showLoading('Loading data ...');

              trainingAPI.getDrillDownChartData({ 
                date: date,
                position,
                player_group: playerGroup,
                sessions: session,
                drilldown_type: isForcePlateMetric ? 'forceplate' : 'testing' ,
                metric_id: metric.is_user_defined ? `c${metric.metric_id}` : metric.metric_id,
                player_id: selectedPlayerId || null
              })
                .then(response => {
                  const data = response.player_data;
                  chart.drilldownData = data;
                  chart.positionData = response.position_data;
                  chart.drilldownResponse = response;
                  let respMetrics = response?.metrics || [{}];
                  const chartMetric = {...metric, ...respMetrics[0]};
                  const {drilldowns, positions} = createDrilldownStructure(data, chartMetric, e.point.name, enableDrillDown, 
                                                                            1, stat, chart.positionData);
                  positionsArray = [...positions]
                  const xAxis = chart.xAxis[0];
                  xAxis.categories = positions;
                  this.options.custom.currentCategories = positions;
                  this.options.custom.drilldown = `${e.point.name}_positional`;
                  const series = drilldowns[e.point.name];  
                  chart.hideLoading();
                  chart.addSingleSeriesAsDrilldown(e.point, series);
                  let secondarySeriesDrilldown;
                  
                  if (isForcePlateMetric) {
                    secondarySeriesDrilldown = drilldowns[`${e.point.name}_secondarySeries`];  
                    chart.addSingleSeriesAsDrilldown(e.point, secondarySeriesDrilldown);
                  } 
                  
                  if (isTestingMetric && isVBT && barWeightMetric) {
                    secondarySeriesDrilldown = drilldowns[`${e.point.name}_secondarySeries`];  
                    chart.addSingleSeriesAsDrilldown(e.point, secondarySeriesDrilldown);
                  }
                  chart.applyDrilldown();  
                  if (isTestingMetric && isVBT && barWeightMetric) {
                    updateYAxisMax(chart, [...secondarySeriesDrilldown.data.map(y => { 
                      return { y };
                     })], 1);
                  } else if (isForcePlateMetric) {
                    updateYAxisMax(chart, [...series.data, ...secondarySeriesDrilldown.data.map((x) => {
                      return { y: x[1]}
                    })], 0);
                  } else {
                    updateYAxisMax(chart, series.data, 0);
                  } 

                  xAxis.setCategories(positions, false);
                  getXAxisLabelRotation(this, positions);
                })
                .catch(error => {
                  console.error('Error fetching drill-down data:', error);
                  chart.hideLoading();
                })
                .finally(() => {
                  this.isDrilling = false;
                });
            } else {
              try {
                let respMetrics = chart.drilldownResponse?.metrics || [{}];
                const chartMetric = {...metric, ...respMetrics[0]};
                const { drilldowns } = createDrilldownStructure(chart.drilldownData, chartMetric, e.point.name, 
                                                                enableDrillDown, chart.drilldownLevel + 1, stat, chart.positionData);
                const series = drilldowns[e.point.name];
                const xAxis = chart.xAxis[0];
                xAxis.categories = series?.data?.map(item => item.name);
                this.options.custom.currentCategories = xAxis.categories;
                let secondarySeriesDrilldown;
                if (chart.drilldownLevel === 1 ) {
                  playerNamesArray = [...xAxis.categories];
                  this.options.custom.drilldown = `${e.point.name}_player`;
                } else if (chart.drilldownLevel === 2 ) {
                  this.options.custom.drilldown = `${e.point.name}_player_reps`;
                }

                chart.addSingleSeriesAsDrilldown(e.point, series);
                if (isForcePlateMetric) {
                  secondarySeriesDrilldown = drilldowns[`${e.point.name}_secondarySeries`];  
                  chart.addSingleSeriesAsDrilldown(e.point, secondarySeriesDrilldown);
                } 

                if (isTestingMetric && isVBT && barWeightMetric) {
                  secondarySeriesDrilldown = drilldowns[`${e.point.name}_secondarySeries`];  
                  chart.addSingleSeriesAsDrilldown(e.point, secondarySeriesDrilldown);
                }
                
                chart.applyDrilldown();
                if (isTestingMetric && isVBT && barWeightMetric) {
                  updateYAxisMax(chart, series.data, 0);
                  updateYAxisMax(chart, [...secondarySeriesDrilldown.data.map(y => { 
                    return { y };
                   })], 1);
                } else if (isForcePlateMetric) {
                   updateYAxisMax(chart, [...series.data, ...secondarySeriesDrilldown.data.map((x) => {
                      return { y: x[1]}
                    })], 0);
                } else {
                  updateYAxisMax(chart, series.data, 0);
                }

                xAxis.setCategories(series?.data?.map(item => item.name), false);
                getXAxisLabelRotation(this, series?.data?.map(item => item.name));
                chart.redraw();
              } catch(error){
                console.error(`Error occurred while setting drill-down level ${chart.drilldownLevel + 1} data:`, error);
                chart.hideLoading();
              }
              finally{
                this.isDrilling = false;
              } 
            }
            chart.drilldownLevel++;
            lastLevel = chart.drilldownLevel;
          }
        },
        drillup: function(e) {
          const levelNum = e.seriesOptions._levelNumber;

          if (lastLevel === levelNum || this.isDrilling) return;
          lastLevel = levelNum;
          const chart = this;  
          chart.isDrilling = true;
          this.options.custom = this.options.custom || {};
          chart.drilldownLevel--;
          
          try{
            if (levelNum=== 2) {
              const xAxis = chart.xAxis[0];
              xAxis.categories = playerNamesArray;
              this.options.custom.currentCategories = playerNamesArray;
              this.options.custom.drilldown = `${e.seriesOptions.id}_player`;
              xAxis.setCategories(playerNamesArray, false);
              getXAxisLabelRotation(this, playerNamesArray);
            } else if (levelNum === 1) {
              const xAxis = chart.xAxis[0];
              xAxis.categories = positionsArray;
              this.options.custom.currentCategories = positionsArray;
              this.options.custom.drilldown = `${e.seriesOptions.id}_positional`;
              xAxis.setCategories(positionsArray, false);
              getXAxisLabelRotation(this, positionsArray);
            } else if (levelNum === 0) {
              if (chart?.drilldownData) delete chart?.drilldownData;
              const xAxis = chart.xAxis[0];
              xAxis.categories = categories
              this.options.custom.currentCategories = categories;
              this.options.custom.drilldown = "";
              xAxis.setCategories(categories, false);
              getXAxisLabelRotation(this, categories);
            }
            rescaleYAxisToData(chart.yAxis[0]);
            if (chart.yAxis.length > 1) {
              rescaleYAxisToData(chart.yAxis[1]);
            }
          } catch(error){
            console.error(`Error occurred while setting drill-up level ${levelNum} data:`, error);
            chart.hideLoading();
          }finally {
            this.isDrilling = false;
          }
        }
      }
    },
    drilldown: {
      activeAxisLabelStyle: {
        cursor: 'pointer',
        color: 'rgb(51, 51, 51)',
        textDecoration: 'none',
        fontWeight: 500,
        tickmarkPlacement: categories.length > 10 ? 'on' : 'between',
        tickWidth: 1
      },
      activeDataLabelStyle: {
        cursor: 'pointer',
        color: 'rgb(51, 51, 51)',
        textDecoration: 'none',
      },
      series: []
    },
    title: {
      text: (showTitle ? (metric?.name || metric?.short || '') : '')
    },
    xAxis: {
      categories,
      gridLineWidth: 2,
      lineColor: '#ccd6eb',  
      lineWidth: 1, 
      labels: categories.length > 20
      ? {
          rotation: -90,
          overflow: 'justify',
          staggerLines: 1,
          step: 0
        }
      : categories.length > 10
      ? {
          rotation: -45,
          overflow: 'justify',
          staggerLines: 1,
          step: 0
        }
      : {
          rotation: 0,
        },
      tickmarkPlacement: categories.length > 10 ? 'on' : 'between',
      tickWidth: 1
    },
    yAxis: [
      {
        title: {
          text: yAxisTitle
        },
        min: Math.min(0, minimumSeriesValue),
        max: Math.max(0, maximumSeriesValue),
        tickAmount: 6,
        plotLines: [],
      }
    ],
    legend: {
      enabled: true,
      verticalAlign: 'top',
      layout: 'horizontal'
    },
    tooltip: {
      ...tooltip,
      formatter: function() {
        if (this.point.isRestOrTravel) {
          return false;
        }
        let s = '<b>' + ((Number.isInteger(this.x) && categories[this.x] !== undefined) ? categories[this.x] : this.x) + '</b>';
        this.points.forEach(function(point) {
          if (point?.point?.max && point?.point?.min) {
            let statStr = stat || "avg";
            if (stat === "max") {
              statStr = "best";
            }
            s += `<br/>${point?.series?.name} (${statStr.charAt(0).toUpperCase() + statStr.slice(1)}): ` + point?.y;
            s += `<br/>${point?.series?.name} (Max): ` + point?.point?.max ;
            s += `<br/>${point?.series?.name} (Min): ` + point?.point?.min ;
          }

          else if(point?.series?.name === 'Error') {
            s += '<br> Max: ' + point?.point?.high ;
            s += '<br> Min: ' + point?.point?.low ;
          } else {
            s += '<br/>' + point.series.name + ': ' + point.y;
          }
        });
        return s;
      }
    },
    plotOptions: {
      column: {
        borderWidth: 0,
        groupPadding: 0.05,
        pointPadding: 0.05,
        dataLabels: {
          enabled: true,
          rotation: categories.length > 20 ? 270 : 0,
          y: categories.length > 20 ? 10 : 0,
          inside: true
        }
      }
    },
    colors: teamColors,
    series: [mainSeries, secondarySeries],
    exporting: {
      enabled: showHamburger,
      ...chartExportableOptions
    }
  };

  function createDrilldownStructure(data, metric, selectedPointName, enableDrillDown = false, levelNum, stat = 'Avg', positionColors = null) {
    const drilldowns = {};
    const positions = [...new Set(data.map(item => item.player_id__position))];
    
    const decimal_places = metric?.num_decimals || 2;
    let barWeightMetricData = [], secondarySeriesData = [];
    const isBarWtOnlyChart = metric.name === barWeightMetric;
    const suffix = getSuffixByStat(metric, stat);

    const metric_name = metric.name;
    let secondarySeries = {
      dataLabels: {
        style: {
          textOutline: 'none'
        }
      }
    };

    if (levelNum == 1) {
      // Second layer: Positions
      let maxVal = -Infinity, minVal = Infinity, bestVal;
      positions.forEach((position) => {
        const positionData = data.filter(item => item.player_id__position === position);
        positionData.forEach((item) => {
          maxVal = Math.max(maxVal, (isForcePlateMetric ? item[`${metric.name}__max`] : item[`${metric.name}__avg`]));
          minVal = Math.min(minVal, (isForcePlateMetric ? item[`${metric.name}__min`] : item[`${metric.name}__avg`]));
        });
      });
      bestVal = metric?.low_good ? min_value : max_value;

      drilldowns[selectedPointName] = {
        name: 'Positions ',
        id: selectedPointName,
        yAxis: 0,
        xAxis: 0,
        data: positions.map((position, index) => {
          const positionData = data.filter(item => item.player_id__position === position);
          const valueOf = (item) => item[`${metric.name}${suffix}`];
          let yVal = null;
          if (isBarWtOnlyChart && useMaxBarWt) {
            yVal = healthyMax(positionData, valueOf, injuredInGroup);
          } else if (suffix.endsWith('avg')) {
            yVal = healthyMean(positionData, valueOf, injuredInGroup);
          } else if (suffix.endsWith('max')) {
            yVal = healthyMax(positionData, valueOf, injuredInGroup);
          } else if (suffix.endsWith('min')) {
            yVal = healthyMin(positionData, valueOf, injuredInGroup);
          } else if (suffix.endsWith('sum')) {
            yVal = healthySum(positionData, valueOf, injuredInGroup);
          }

          if (isTestingMetric && isVBT && barWeightMetric) {
            const barWtOf = (item) => item[`${barWeightMetric}${suffix}`];
            const barWtValue = useMaxBarWt ? healthyMax(positionData, barWtOf, injuredInGroup) : healthyMean(positionData, barWtOf, injuredInGroup);
            barWeightMetricData.push(barWtValue === null ? null : Number(barWtValue.toFixed(decimal_places)));
          }

          if (isForcePlateMetric) {
            const max = healthyMax(positionData, (item) => item[`${metric.name}__max`], injuredInGroup);
            const min = healthyMin(positionData, (item) => item[`${metric.name}__min`], injuredInGroup);
            secondarySeriesData.push([min, max]);
          }

          return {
            name: position,
            y: yVal === null ? null : Number(yVal.toFixed(decimal_places)),
            ...(enableDrillDown && {drilldown: position}),
            color: positionColors?.[position]?.color
          };
        }),
      };

      if (isForcePlateMetric) {
        secondarySeries = {
          ...secondarySeries,
          name: 'Error',
          type: 'errorbar',
          data: secondarySeriesData,
          yAxis: 0,
          color: 'black', 
          stemColor: 'black', 
          whiskerColor: 'black' 
        }

        drilldowns[`${selectedPointName}_secondarySeries`] = secondarySeries;
      }

      if(isTestingMetric && isVBT && barWeightMetric){
        secondarySeries = {
          ...secondarySeries,
          name: barWeightMetric,
          id: `${selectedPointName}_secondarySeries`,
          type: 'spline',
          data: barWeightMetricData,
          color: 'rgb(70, 170, 197)',
          yAxis: 1,
          showInLegend: barWeightMetricData.length ? true : false,
        }

        drilldowns[`${selectedPointName}_secondarySeries`] = secondarySeries
      }

      return{ drilldowns, positions };
    }
    
    if (levelNum == 2) { 
      // Third layer: Players
      let maxVal = -Infinity, minVal = Infinity, bestVal;
      positions.filter(pos => pos === selectedPointName).forEach((position) => {
        const playersData = data.filter(item => item.player_id__position === position);
        playersData.forEach((item) => {
          maxVal = Math.max(maxVal, (isForcePlateMetric ? item[`${metric.name}__max`] : item[`${metric.name}__avg`]));
          minVal = Math.min(minVal, (isForcePlateMetric ? item[`${metric.name}__min`] : item[`${metric.name}__avg`]));
        });
      });
      bestVal = metric?.low_good ? min_value : max_value;

      positions.filter(pos => pos === selectedPointName).forEach((position) => {
        const playersData = data
          .filter(item => item.player_id__position === position)
          .map(player => {
            const playerName = drilldownPlayerLabel(player);
            const color = player.color

            if (isTestingMetric && isVBT && barWeightMetric) {
              barWeightMetricData.push(Number(player[`${barWeightMetric}${suffix}`]?.toFixed(decimal_places)));
            }

            if (isForcePlateMetric) {
              let max = -Infinity, min = Infinity 
              //playersData
              player.reps[metric.name].forEach((item) => {
                max = Math.max(max, item);
                min = Math.min(min, item)
              });
              secondarySeriesData.push([min, max]);
            }

            let yVal;
            if (suffix.endsWith('avg')) {
              yVal = player[`${metric_name}${suffix}`];
            } else if (suffix.endsWith('max')) {
              yVal = player.reps[metric_name].reduce((max, item) => Math.max(max, (Array.isArray(item) ? item[0] : item)), -Infinity);
            } else if (suffix.endsWith('min')) {
              yVal = player.reps[metric_name].reduce((min, item) => Math.min(min, (Array.isArray(item) ? item[0] : item)), Infinity);
            } else if (suffix.endsWith('sum')) {
              yVal = player.reps[metric_name].reduce((sum, item) => sum + (Array.isArray(item) ? item[0] : item), 0);
            }
            
            return {
              name: playerName,
              y: Number(yVal?.toFixed(decimal_places)),
              custom: { injured: injuredInGroup(player) },
              ...(enableDrillDown && {drilldown: playerName}),
              color
            };
          });
      
        drilldowns[position] = {
          name: `Players`,
          id: position,
          xAxis: 0,
          yAxis: 0,
          data: playersData
        };

        if (isForcePlateMetric) {
          secondarySeries = {
            ...secondarySeries,
            name: 'Error',
            type: 'errorbar',
            data: secondarySeriesData,
            yAxis: 0,
            color: 'black', 
            stemColor: 'black', 
            whiskerColor: 'black' 
          }

          drilldowns[`${selectedPointName}_secondarySeries`] = secondarySeries
      }

        if(isTestingMetric && isVBT && barWeightMetric){
          secondarySeries = {
            ...secondarySeries,
            name: barWeightMetric,
            id: `${selectedPointName}_secondarySeries`,
            type: 'spline',
            data: barWeightMetricData,
            color: 'rgb(70, 170, 197)',
            yAxis: 1,
            showInLegend: barWeightMetricData.length ? true : false,
          }

          drilldowns[`${selectedPointName}_secondarySeries`] = secondarySeries
        }
      });

      return{ drilldowns };
    }

    if (levelNum == 3) { 
      // Fourth layer: Player Reps
      let maxVal = -Infinity, minVal = Infinity, bestVal;
      data.filter(i => {
        const playerName = drilldownPlayerLabel(i);
        if (playerName === selectedPointName) return i;
      }).forEach((item) => {
        if (isTestingMetric && isVBT && barWeightMetric) {
          item.reps[metric.name].forEach(([r, trial]) => {
            maxVal = Math.max(maxVal, r);
            minVal = Math.min(minVal, r);
          }); 
        } else {
          item.reps[metric.name].forEach((r) => {
            maxVal = Math.max(maxVal, r);
            minVal = Math.min(minVal, r);
          }); 
        }
      });
      bestVal = metric?.low_good ? min_value : max_value;

      data.filter(i => {
        const playerName = drilldownPlayerLabel(i);
        if (playerName === selectedPointName) return i;
      }).forEach((item) => {
        const playerName = drilldownPlayerLabel(item);
        const color = item.color

        let playerRepsData;
        if (isTestingMetric && isVBT && barWeightMetric) {
          playerRepsData = item.reps[metric.name].map(([repVal, trial], idx) => { 
            if (isForcePlateMetric) {
              let max = repVal, min = repVal;
              secondarySeriesData.push([min, max]);
            }

            return {
              name: trial,
              y: Number(repVal?.toFixed(decimal_places)),
              color
            };
          })
        } else {
          playerRepsData = item.reps[metric.name].map((repVal, idx) => { 
            if (isForcePlateMetric) {
              let max = repVal, min = repVal;
              secondarySeriesData.push([min, max]);
            }

            return {
              name: `Rep-${idx + 1}`,
              y: Number(repVal?.toFixed(decimal_places)),
              color
            };
          })
        }

        if (isTestingMetric && isVBT && barWeightMetric) {
          barWeightMetricData = item.reps[barWeightMetric].map(([repVal, trial], idx) => { 
            return Number(repVal?.toFixed(decimal_places));
          })
        }
        
        drilldowns[playerName] = {
          name: `Player Reps`,
          id: playerName,
          data: playerRepsData,
          xAxis: 0,
          yAxis: 0,
        };

        if (isForcePlateMetric) {
          secondarySeries = {
            ...secondarySeries,
            name: 'Error',
            type: 'errorbar',
            data: secondarySeriesData,
            yAxis: 0,
            color: 'black', 
            stemColor: 'black', 
            whiskerColor: 'black' 
          }

          drilldowns[`${selectedPointName}_secondarySeries`] = secondarySeries
        }

        if(isTestingMetric && isVBT && barWeightMetric){
          secondarySeries = {
            ...secondarySeries,
            name: barWeightMetric,
            id: `${selectedPointName}_secondarySeries`,
            type: 'spline',
            data: barWeightMetricData,
            color: 'rgb(70, 170, 197)',
            yAxis: 1,
            showInLegend: barWeightMetricData.length ? true : false,
          }

          drilldowns[`${selectedPointName}_secondarySeries`] = secondarySeries
        }
      });

      return{ drilldowns };
    }
  }

  function updateYAxisMax(chart, seriesData, seriesIdx) {
    if(!seriesData) return
    if((seriesIdx === 0 || seriesIdx === 1) && Array.isArray(seriesData)){
      // Ignore null/undefined/NaN points (e.g. UDM output with missing rep data) so
      // one bad point doesn't leave the drilldown stuck on the parent chart's scale
      const values = seriesData.map(point => point?.y).filter(v => typeof v === 'number' && isFinite(v));
      if(!values.length) return

      const newMin = Math.min(0, Math.min(...values) * 1.1);
      const newMax = Math.max(0, Math.max(...values) * 1.1);
      if(newMin === newMax) return

      chart.yAxis[seriesIdx].setExtremes(newMin, newMax);
    }
  }

  function rescaleYAxisToData(axis) {
    const { dataMin, dataMax } = axis.getExtremes();
    if(!isFinite(dataMin) || !isFinite(dataMax)) return

    const newMin = Math.min(0, dataMin * 1.1);
    const newMax = Math.max(0, dataMax * 1.1);
    if(newMin === newMax) return

    axis.setExtremes(newMin, newMax);
  }

  if(!isForcePlateMetric) {
    const secondYAxis = {
      title: {
        text: ''
      },
      min: secondaryMax < 0 ? secondaryMax : 0,
      max: secondaryMax < 0 ? 0 : secondaryMax,
      opposite: true,
      gridLineWidth: 0,
      id: 'bodyweight',
      plotLines: []
    }

    options.yAxis.push(secondYAxis)
  }

  if(showPredictions) {
    options = addPredictionSeriesConfig(options, metric.name, metric.key, predictionSeries, null, series, dataColorUtil, selectedPlayerId)
  }

  return options;
};

export const getCustomTooltip = (isForcePlateMetric, isSubjectiveMetric, isRPEChart, isTestingMetric, tooltip) => {
  if(isForcePlateMetric) {
    return {
      ...tooltip,
      formatter: function () {
        return `<strong>${this.x}</strong> <br> Avg: ${this.point.y} <br> Max: ${this.point.max} <br> Min: ${this.point.min} <br> Diff: ${this.point.diff}`
      }
    }
  }
  if (isTestingMetric) {
    return {
      ...tooltip,
      formatter: function () {
        return `<p>${JSON.stringify(this.point)} ${this.x} ${this.y}</p>`
      }
    }
  }

  return tooltip
}